import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Mail, MessageSquare, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BusinessCampaignsPage = () => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [selectedType, setSelectedType] = useState('email');

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/business/${user.id}`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Error al kargar kampañas');
    } finally {
      setLoading(false);
    }
  };

  const generateCampaign = async () => {
    if (!user?.isPremium) {
      toast.error('Necesitas una suscripción Premium para usar kampañas IA');
      return;
    }

    setGenerating(true);
    setGeneratedContent('');
    
    try {
      const response = await axios.post(
        `${API}/campaigns/generate?campaign_type=${selectedType}`,
        {},
        {
          headers: {
            'X-Business-Id': user.id
          }
        }
      );
      
      setGeneratedContent(response.data.content);
      toast.success('¡Kampaña generada!');
      fetchCampaigns();
    } catch (error) {
      console.error('Error generating campaign:', error);
      toast.error('Error al generar kampaña');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-manrope font-bold text-3xl sm:text-4xl mb-2">Kampañas IA</h1>
          <p className="text-muted-foreground">Genera kampañas de marketing automatizadas</p>
        </div>

        {/* Premium Check */}
        {!user?.isPremium ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-reward-gold to-reward-gold/80 rounded-3xl p-12 text-center text-white"
          >
            <Sparkles className="w-16 h-16 mx-auto mb-6" />
            <h2 className="font-manrope font-bold text-3xl mb-4">
              Desblokea Kampañas IA
            </h2>
            <p className="text-white/90 mb-8 max-w-2xl mx-auto">
              Aktualiza a Premium para acceder a kampañas automatizadas de email y SMS generadas por IA,
              diseñadas para reaktivar klientes y aumentar tus ventas semanales.
            </p>
            <Button className="bg-white text-reward-gold hover:bg-white/90 rounded-full px-8 py-6 text-lg">
              Actualizar a Premium - $297/mes
            </Button>
          </motion.div>
        ) : (
          <>
            {/* Campaign Generator */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card rounded-2xl p-6 shadow-card border border-border/50 mb-8"
            >
              <h2 className="font-semibold text-xl mb-4">Generar Nueva Kampaña</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Tipo de Kampaña</label>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setSelectedType('email')}
                      className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                        selectedType === 'email'
                          ? 'border-local-sage bg-local-sage/10'
                          : 'border-border hover:border-local-sage/50'
                      }`}
                      data-testid="campaign-type-email"
                    >
                      <Mail className="w-6 h-6 mx-auto mb-2" />
                      <p className="font-medium">Email</p>
                    </button>
                    <button
                      onClick={() => setSelectedType('sms')}
                      className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                        selectedType === 'sms'
                          ? 'border-local-sage bg-local-sage/10'
                          : 'border-border hover:border-local-sage/50'
                      }`}
                      data-testid="campaign-type-sms"
                    >
                      <MessageSquare className="w-6 h-6 mx-auto mb-2" />
                      <p className="font-medium">SMS</p>
                    </button>
                  </div>
                </div>
                
                <Button
                  onClick={generateCampaign}
                  disabled={generating}
                  className="w-full bg-local-sage hover:bg-local-sage/90 rounded-full py-6"
                  data-testid="generate-campaign-button"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generar Kampaña kon IA
                    </>
                  )}
                </Button>
              </div>
              
              {generatedContent && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-6 p-6 bg-muted/50 rounded-xl"
                  data-testid="generated-campaign"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Kampaña Generada</h3>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => {
                        navigator.clipboard.writeText(generatedContent);
                        toast.success('Kopiado al portapapeles');
                      }}
                    >
                      Kopiar
                    </Button>
                  </div>
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                    {generatedContent}
                  </div>
                </motion.div>
              )}
            </motion.div>

            {/* Campaigns History */}
            <div>
              <h2 className="font-manrope font-bold text-2xl mb-6">Historial de Kampañas</h2>
              
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-local-sage" />
                </div>
              ) : campaigns.length > 0 ? (
                <div className="space-y-4" data-testid="campaigns-list">
                  {campaigns.map((campaign) => (
                    <motion.div
                      key={campaign.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          {campaign.type === 'email' ? (
                            <Mail className="w-5 h-5 text-local-sage" />
                          ) : (
                            <MessageSquare className="w-5 h-5 text-local-sage" />
                          )}
                          <div>
                            <p className="font-semibold">
                              Kampaña de {campaign.type === 'email' ? 'Email' : 'SMS'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(campaign.createdAt).toLocaleDateString('es-ES', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-muted">
                          {campaign.status === 'draft' ? 'Borrador' : 'Enviada'}
                        </span>
                      </div>
                      <div className="bg-muted/50 rounded-xl p-4 max-h-48 overflow-y-auto">
                        <p className="text-sm whitespace-pre-wrap">{campaign.content}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-card rounded-2xl">
                  <Send className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aún no has generado kampañas</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};